import { CarModelRef } from "api"

/**
 * One CMS search hit. Car pages carry the raw Tafnit codes of the vehicle they
 * describe; other content comes back without them.
 */
export interface TermSearchItem {
  manufacturerCode: number
  modelCode: number
  manufacturerName: string
  modelName: string
  masterManufacturerDescription: string
  masterModelDescription: string
  id: number
  name: string
  urlSegment: string
  sortOrder: number
  level: number
  path: string
  createDate: string
  updateDate: string
  key: string
  url: string
  titleBreadcrumbs: string | null
  searchKeyword: string | null
}

export interface TermModelRefs {
  items: TermSearchItem[]
  leasing: CarModelRef[]
  sale: CarModelRef[]
}

/**
 * The CMS pages its hits (10 by default), so a broad term such as a brand name
 * lost every model past the first page. Matches the cars-search API's cap on
 * `rawModels`, so every distinct pair found here can be sent in one request.
 */
const TERM_SEARCH_PAGE_SIZE = 200

function toModelRefs(items: TermSearchItem[], urlSegment: string) {
  const refs = new Map<string, CarModelRef>()
  items
    .filter(
      (item) =>
        item.url.includes(urlSegment) &&
        item.manufacturerCode &&
        item.modelCode,
    )
    .forEach(({ manufacturerCode, modelCode }) =>
      refs.set(`${manufacturerCode}-${modelCode}`, {
        manufacturerCode,
        modelCode,
      }),
    )
  return [...refs.values()]
}

/**
 * Resolves a free-text term to the raw (manufacturer, model) pairs the CMS
 * matched, split by deal type. The pairs go to the cars-search API as
 * `rawModels`, which expands each one to its whole master-model group, so the
 * result no longer needs filtering client-side.
 *
 * Rejects when the CMS cannot be reached, so callers can tell "matched
 * nothing" (empty lists) apart from "could not search".
 */
export async function fetchTermModelRefs(term: string): Promise<TermModelRefs> {
  const CMS_API = process.env.GRAPHQL_URL!.replace("/graphql", "")
  const response = await fetch(
    `${CMS_API}/api/v1/search/search?path=-1&term=${encodeURIComponent(term)}&pageSize=${TERM_SEARCH_PAGE_SIZE}`,
  )
  if (!response.ok) {
    throw new Error(`CMS search failed with status ${response.status}`)
  }

  const data: { items?: TermSearchItem[] } = await response.json()
  const items = data.items || []

  return {
    items,
    leasing: toModelRefs(items, "/leasing/"),
    sale: toModelRefs(items, "/selling/"),
  }
}
